import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Modal,
  DropdownSingle,
  Tooltip,
  OKELink,
  colors2,
  UI_BODY,
  UI_BODY_BOLD,
  UI_BODY_SEMIBOLD,
  UI_BODY_SM,
  UI_CAPTION,
} from "oolib";
// SwitchSingle replaced by DropdownSingle (cron presets — "Off" replaces the boolean toggle).
import {
  ButtonGhost,
  ButtonGhostCompact,
  ButtonPrimary,
  ButtonPrimaryCompact,
} from "oolib/dist/v2/components/Buttons";
import { DisplayIcon } from "oolib/dist/utils/comps/DisplayIcon";
import {
  useExternalConnectionStatus,
  useConnectExternalConnection,
  useUpdateCronConfig,
  useSyncNowExternalConnection,
} from "@/utils/react-query-hooks/externalConnections";
import { useGetAllPublishedAndUnpublishedTpls } from "@/utils/react-query-hooks/tci";
import { __CheckAccessToModule } from "@/utils/accessControl";
import { useGetQueryData } from "@/utils/react-query-hooks/general";

/**
 * Integration Settings modal — manage-mode only.
 *
 * Opens for integrations that are already connected. Two views inside one modal,
 * because an integration can feed several datasets:
 *
 *   LIST      the shared connection summary + every dataset this integration
 *             imports into, plus "Add new dataset". Disconnect lives in this
 *             header — it revokes the credential all the datasets share, so it
 *             belongs to the integration, not to any one dataset.
 *   DATASET   one dataset's own settings: last sync, next auto-sync, its
 *             schedule, Sync now, and Configure (re-run the import picker).
 *             Reached by clicking a row; a back arrow returns to the list.
 *
 * The not-yet-set-up path bypasses this modal entirely and opens the
 * DirectDataImportModal directly (see SM_Integrations/index.js).
 */

// Cron presets — values map to cronConfig.{enabled, frequency}. Off is
// special: it toggles cronConfig.enabled=false; the other presets imply
// enabled=true with the matching frequency string. okf-sub runs three tier
// crons (hourly/daily/weekly) that filter dataset links by these frequency
// values — so two datasets of one integration can sit on different tiers.
const CRON_PRESETS = [
  { display: "Off",     value: "off",      enabled: false, frequency: null },
  { display: "Hourly",  value: "hourly",   enabled: true,  frequency: "hourly" },
  { display: "Daily",   value: "daily",    enabled: true,  frequency: "daily" },
  { display: "Weekly",  value: "weekly",   enabled: true,  frequency: "weekly" },
];
const cronPresetForConfig = (cronConfig) => {
  if (!cronConfig?.enabled) return CRON_PRESETS[0];
  return CRON_PRESETS.find((p) => p.frequency === cronConfig.frequency) || CRON_PRESETS[2]; // default Daily
};

// Next-run computation matches the okf-sub cron schedules. All times UTC.
//   hourly: next :00
//   daily:  next 05:00 UTC
//   weekly: next Sunday 05:00 UTC
export const nextAutoSyncForFrequency = (frequency) => {
  if (!frequency) return null;
  const now = new Date();
  const next = new Date(now);
  next.setUTCSeconds(0, 0);
  if (frequency === "hourly") {
    next.setUTCMinutes(0);
    next.setUTCHours(now.getUTCHours() + 1);
    return next;
  }
  if (frequency === "daily") {
    next.setUTCHours(5, 0, 0, 0);
    if (next <= now) next.setUTCDate(next.getUTCDate() + 1);
    return next;
  }
  if (frequency === "weekly") {
    next.setUTCHours(5, 0, 0, 0);
    const daysUntilSunday = (7 - next.getUTCDay()) % 7;
    next.setUTCDate(next.getUTCDate() + daysUntilSunday);
    if (next <= now) next.setUTCDate(next.getUTCDate() + 7);
    return next;
  }
  return null;
};

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
`;

const Card = styled.div`
  border: 1px solid ${colors2.grey10};
  border-radius: 12px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  min-height: 52px;
  padding: 8px 16px;
  border-bottom: 1px solid ${colors2.grey10};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid ${colors2.grey5};
  &:last-child {
    border-bottom: none;
  }
`;

// A dataset row in the list view. Same geometry as Row but clickable, so the
// whole row is the target rather than a small link inside it.
const DatasetRow = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-bottom: 1px solid ${colors2.grey5};
  background: transparent;
  text-align: left;
  cursor: pointer;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: ${colors2.grey5};
  }
`;

const EmptyState = styled.div`
  padding: 24px 16px;
  text-align: center;
`;

const StatusWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  min-height: 4rem;
  padding: 0.8rem 2rem;
  background-color: white;
  border-bottom: 1px solid ${colors2.grey10};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const FooterBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 24px;
  border-top: 1px solid ${colors2.grey10};
`;

const PausedNotice = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: ${colors2.yellowBG || "#fff8e1"};
  border-bottom: 1px solid ${colors2.grey5};
`;

export const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
};

export const formatDateTime = (date) => {
  if (!date) return null;
  try {
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
};

/**
 * One dataset's settings. A component of its own (not an inline branch) because
 * its schedule and sync mutations are addressed to a single contentType, and
 * hooks can't be called conditionally.
 */
const DatasetSettingsView = ({
  provider,
  name,
  link,
  datasetTitle,
  canPublish,
  providerSupportsSync,
  isConnActive,
}) => {
  const { contentType } = link;
  const {
    mutate: updateCronConfig,
    isLoading: isSavingCron,
    isSuccess: cronJustSaved,
  } = useUpdateCronConfig(provider, contentType);
  // "Sync new data" — immediate incremental pull for THIS dataset. Same
  // eligibility as its Auto-sync schedule below (both need an active connection
  // + an incremental cursor), so we render it alongside the schedule dropdown.
  const { mutate: syncNow, isLoading: isSyncingNow } = useSyncNowExternalConnection(
    provider,
    { providerName: name, contentType }
  );

  // Brief "Saved" indicator after the cron mutation succeeds — fades after 2s.
  // Local state because react-query's `isSuccess` stays true; we want a pulse.
  const [showSavedFlash, setShowSavedFlash] = useState(false);
  useEffect(() => {
    if (cronJustSaved) {
      setShowSavedFlash(true);
      const t = setTimeout(() => setShowSavedFlash(false), 2000);
      return () => clearTimeout(t);
    }
  }, [cronJustSaved]);

  const currentCronPreset = cronPresetForConfig(link.cronConfig);
  const [pendingCronPreset, setPendingCronPreset] = useState(null);
  const shownCronPreset = pendingCronPreset || currentCronPreset;
  const hasPendingCronChange =
    !!pendingCronPreset && pendingCronPreset.value !== currentCronPreset.value;
  const handleCronChange = (_k, preset) => {
    if (!preset) return;
    setPendingCronPreset(preset);
  };
  const handleCronApply = () => {
    if (!hasPendingCronChange || isSavingCron) return;
    const preset = pendingCronPreset;
    // Send both enabled + frequency so the backend's $set updates both
    // atomically. For "Off" we also null out frequency so the stored state
    // reflects the user's choice.
    updateCronConfig(
      preset.enabled
        ? { enabled: true, frequency: preset.frequency }
        : { enabled: false, frequency: null }
    );
  };
  useEffect(() => {
    if (pendingCronPreset && pendingCronPreset.value === currentCronPreset.value) {
      setPendingCronPreset(null);
    }
  }, [pendingCronPreset, currentCronPreset.value]);

  const lastSyncLabel = link.lastImport?.at
    ? `${formatDateTime(link.lastImport.at)}`
      + `${typeof link.lastImport.rowCount === "number" ? ` · ${link.lastImport.rowCount} rows` : ""}`
      + `${link.lastImport.source ? ` · ${link.lastImport.source === "cron" ? "auto" : "manual"}` : ""}`
    : "No imports yet";
  const nextAutoSyncAt = link.cronConfig?.enabled
    ? nextAutoSyncForFrequency(link.cronConfig.frequency)
    : null;

  // "Sync new data" eligibility. Independent of whether a schedule is enabled
  // — this is an on-demand pull. Requires: (a) the provider's adapter actually
  // supports incremental sync (supportsIncrementalSync from the status endpoint;
  // providers like Typeform/Discord/Airtable don't, and never seed a cursor —
  // so we must gate on this, not on cursor presence), (b) an active connection,
  // and (c) an incremental cursor for THIS dataset (startSyncFrom, seeded by its
  // first successful import).
  const hasSyncCursor = !!link.cronConfig?.startSyncFrom;
  const canSyncNow = canPublish && providerSupportsSync && isConnActive && hasSyncCursor;
  // A sync counts as in-flight if EITHER this tab just kicked one off
  // (isSyncingNow — instant, but lost on modal unmount) OR the link row says one
  // is running (link.syncInProgress — durable, survives close/reopen and covers
  // cron + other tabs). Combining both gives instant feedback on click AND a
  // persistent "Syncing…" state when the modal is reopened mid-sync.
  const isSyncing = isSyncingNow || !!link.syncInProgress;
  // Reasons the button is disabled WHEN SHOWN. Providers that don't support
  // incremental sync don't reach here — the whole row is hidden on
  // !providerSupportsSync (see render below) — so we only cover the transient
  // states that can still become true.
  const syncNowDisabledReason = !canPublish
    ? "You don't have permission to run a sync."
    : !isConnActive
      ? "Connection is not active — reconnect first."
      : !hasSyncCursor
        ? "Run a manual import first to enable instant sync."
        : null;

  return (
    <Card>
      <CardHeader>
        <UI_BODY_BOLD>Imports</UI_BODY_BOLD>
        {/* On-demand incremental sync. Shown only for providers whose adapter
            supports incremental sync; disabled + tooltip on the transient
            reasons (no permission / stale / cursor not seeded); "Syncing…"
            while a sync is in flight (this tab or server-side). */}
        {canPublish && providerSupportsSync && (
          syncNowDisabledReason ? (
            <Tooltip text={syncNowDisabledReason} position="left">
              {/* title on a wrapping span guarantees the reason is
                  discoverable even if the disabled button swallows
                  pointer events (disabled elements don't fire hover). */}
              <span title={syncNowDisabledReason}>
                <ButtonGhostCompact icon="ArrowsClockwise" disabled>
                  Sync Now
                </ButtonGhostCompact>
              </span>
            </Tooltip>
          ) : (
            <ButtonGhostCompact
              icon="ArrowsClockwise"
              style={{ backgroundColor: colors2.grey5 }}
              onClick={() => syncNow()}
              disabled={!canSyncNow || isSyncing}
            >
              {isSyncing ? "Syncing…" : "Sync Now"}
            </ButtonGhostCompact>
          )
        )}
      </CardHeader>

      {/* Explains a schedule reading "Off" that the user did not switch off.
          Set when repeated failures auto-paused this dataset; cleared when the
          user turns the schedule back on. */}
      {link.autoPausedReason && (
        <PausedNotice>
          <DisplayIcon icon="WarningCircle" size={16} color="#d99100" />
          <UI_CAPTION style={{ color: colors2.grey80 }}>
            {`Auto-sync was switched off after ${link.autoPausedReason}. `}
            {"Fix the cause, then set a schedule again below."}
          </UI_CAPTION>
        </PausedNotice>
      )}

      <Row>
        <UI_BODY_SM style={{ color: colors2.grey60 }}>Dataset</UI_BODY_SM>
        <UI_BODY_SM style={{ display: "inline" }}>
          <OKELink
            to={`/datasets/${contentType}`}
            text={datasetTitle}
            iconAfter="ArrowLineUpRight"
            color="black"
          />
        </UI_BODY_SM>
      </Row>
      <Row>
        <UI_BODY_SM style={{ color: colors2.grey60 }}>Last sync</UI_BODY_SM>
        <UI_BODY_SM>{lastSyncLabel}</UI_BODY_SM>
      </Row>
      {nextAutoSyncAt && (
        <Row>
          <UI_BODY_SM style={{ color: colors2.grey60 }}>Next auto-sync</UI_BODY_SM>
          <UI_BODY_SM>{formatDateTime(nextAutoSyncAt)}</UI_BODY_SM>
        </Row>
      )}
      {/*
        Hide the "Auto-sync schedule" option for integrations that
        don't have this feature wired up in the backend.
      */}
      {providerSupportsSync && (
        <Row>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <UI_BODY_SM>Auto-sync schedule</UI_BODY_SM>
              {isSavingCron && (
                <UI_CAPTION style={{ color: colors2.grey60 }}>Saving…</UI_CAPTION>
              )}
              {!isSavingCron && showSavedFlash && (
                <UI_CAPTION style={{ color: "#1ea672" }}>Saved</UI_CAPTION>
              )}
            </div>
            <UI_CAPTION style={{ color: colors2.grey60 }}>
              Pull new data into this dataset on a schedule.
            </UI_CAPTION>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <DropdownSingle
              options={CRON_PRESETS}
              value={shownCronPreset}
              onChange={canPublish ? handleCronChange : undefined}
              readOnly={!canPublish}
              genCustomSelectComp={() => (
                <ButtonGhostCompact
                  iconAfter="CaretDown"
                  style={{ border: `1px solid ${colors2.grey10}` }}
                >
                  {shownCronPreset?.display}
                </ButtonGhostCompact>
              )}
            />
            {canPublish && (
              <ButtonPrimaryCompact
                onClick={handleCronApply}
                disabled={!hasPendingCronChange || isSavingCron}
                style={
                  hasPendingCronChange && !isSavingCron
                    ? { backgroundColor: "#000", color: "white" }
                    : undefined
                }
              >
                Apply
              </ButtonPrimaryCompact>
            )}
          </div>
        </Row>
      )}
    </Card>
  );
};

export const IntegrationSettingsModal = ({
  provider,
  name,
  Icon,
  onClose,
  onDisconnectRequest,
  onSyncRequest,
  onAddDatasetRequest,
}) => {
  // ACL: PUBLISH gates writes (schedule, Sync now, Add new dataset). DELETE
  // gates Disconnect. Users without either can still view the modal contents
  // (read-only).
  const userData = useGetQueryData("userData");
  const canPublish = __CheckAccessToModule(userData?.user, "PUBLISH", "integrations");
  const canDelete = __CheckAccessToModule(userData?.user, "DELETE", "integrations");

  // Which dataset's settings are open. null = the list view.
  const [openContentType, setOpenContentType] = useState(null);

  // Poll status ONLY while some dataset's sync is in progress, so the "Syncing…"
  // button clears when the sync finishes even in cases the socket
  // cache-invalidation doesn't cover (e.g. a NO_DATA no-op fans out no batches,
  // so no completion event fires). refetchInterval takes a function: return an
  // interval while any link is syncing, false to stop polling once they all
  // settle — self-limiting, no extra state or effect needed.
  const { data: conn, isLoading, error } = useExternalConnectionStatus(provider, {
    refetchInterval: (latest) =>
      (latest?.links || []).some((l) => l.syncInProgress) ? 3000 : false,
  });
  const { connect, isConnecting } = useConnectExternalConnection(provider, {
    providerName: name,
  });

  const links = useMemo(() => conn?.links || [], [conn?.links]);

  // Look up each dataset's tpl so we can show its human-readable title instead
  // of the camelCase kp_content_type identifier.
  const { data: allTpls } = useGetAllPublishedAndUnpublishedTpls();
  const titleFor = (contentType) => {
    const tpl = allTpls?.find((t) => t.kp_content_type === contentType);
    return tpl?.general?.content?.title || contentType;
  };

  // Hand off to the parent — it owns the confirmation modal that's part of
  // the per-integration collab lock. We leave Settings open behind the
  // confirm modal so a cancel returns the user to context; the useEffect
  // below auto-closes Settings once the disconnect actually succeeds.
  const handleDisconnect = () => {
    if (onDisconnectRequest) onDisconnectRequest(provider);
  };

  // Auto-close when the connection goes away (disconnect succeeded — either
  // via this user's confirm modal or via another user's parallel action).
  useEffect(() => {
    if (!isLoading && conn && conn.connected === false) {
      onClose();
    }
  }, [isLoading, conn?.connected, onClose, conn]);

  // If the dataset we're looking at disappears (deleted elsewhere, or the whole
  // integration was re-pointed), fall back to the list rather than rendering an
  // empty settings view.
  const openLink = links.find((l) => l.contentType === openContentType) || null;
  useEffect(() => {
    if (openContentType && !openLink) setOpenContentType(null);
  }, [openContentType, openLink]);

  const connectedAt = formatDate(conn?.connectedAt);
  const isConnActive = conn?.status === "active";
  const isStale = !!conn && conn.status === "stale";
  const providerSupportsSync = !!conn?.supportsIncrementalSync;

  const errorMsg = error?.response?.data?.error || error?.message;

  const showingDataset = !!openLink && !isStale;

  return (
    <Modal
      fitToContentHeight={true}
      desktopWidth="640px"
      onClose={onClose}
      title={`${name} — Settings`}
      disableHeader={true}
    >
      <Header>
        <HeaderTitle>
          {showingDataset ? (
            <>
              <ButtonGhost icon="ArrowLeft" onClick={() => setOpenContentType(null)} />
              <UI_BODY_SEMIBOLD>{titleFor(openLink.contentType)}</UI_BODY_SEMIBOLD>
            </>
          ) : (
            <>
              {Icon && <Icon size={24} />}
              <UI_BODY_SEMIBOLD>{name}</UI_BODY_SEMIBOLD>
            </>
          )}
        </HeaderTitle>
        <HeaderActions>
          {/* Configure re-runs the import picker (channels, date range) and
              appends the result to THIS dataset, so it only shows while a
              dataset is open. Same header slot the single-dataset modal used
              before this screen was split in two. */}
          {showingDataset && canPublish && onSyncRequest && (
            <ButtonGhostCompact
              icon="PencilSimple"
              style={{ backgroundColor: colors2.grey5 }}
              onClick={() => onSyncRequest(provider, openLink.contentType)}
            >
              Configure
            </ButtonGhostCompact>
          )}
          {/* Disconnect revokes the credential every dataset shares, so it
              belongs to the integration and only shows on the list view. */}
          {!showingDataset && !isLoading && conn && canDelete && (
            <ButtonGhostCompact
              icon="X"
              iconColor={colors2.red}
              style={{
                backgroundColor: colors2.redBG,
                color: colors2.red,
              }}
              onClick={handleDisconnect}
            >
              Disconnect
            </ButtonGhostCompact>
          )}
          <ButtonGhost icon="X" onClick={onClose} />
        </HeaderActions>
      </Header>

      <Body>
        {isLoading && <UI_BODY style={{ color: colors2.grey60 }}>Loading…</UI_BODY>}

        {errorMsg && (
          <UI_BODY style={{ color: colors2.redText }}>{errorMsg}</UI_BODY>
        )}

        {/* A stale credential can't fetch anything, so there is nothing
            actionable in either view until it is renewed. Replace the whole body
            with the reauthenticate prompt rather than showing datasets whose
            every control is disabled. */}
        {!isLoading && isStale && (
          <Card>
            <CardHeader>
              <UI_BODY_BOLD>Authentication expired</UI_BODY_BOLD>
              <StatusWrap>
                <UI_BODY_SM style={{ color: colors2.red }}>Stale</UI_BODY_SM>
                <DisplayIcon icon="XCircle" size={16} color={colors2.red} />
              </StatusWrap>
            </CardHeader>
            <EmptyState>
              <UI_BODY_SM style={{ color: colors2.grey60 }}>
                {`${name} needs to be authenticated again before any of its datasets can sync. `}
                {"Your datasets and their schedules are kept."}
              </UI_BODY_SM>
              {canPublish && (
                <div style={{ marginTop: 16 }}>
                  <ButtonPrimary
                    onClick={() => connect()}
                    isLoading={isConnecting}
                    disabled={isConnecting}
                  >
                    Authenticate
                  </ButtonPrimary>
                </div>
              )}
            </EmptyState>
          </Card>
        )}

        {!isLoading && conn && !isStale && showingDataset && (
          <DatasetSettingsView
            provider={provider}
            name={name}
            link={openLink}
            datasetTitle={titleFor(openLink.contentType)}
            canPublish={canPublish}
            providerSupportsSync={providerSupportsSync}
            isConnActive={isConnActive}
          />
        )}

        {!isLoading && conn && !isStale && !showingDataset && (
          <>
            <Card>
              <CardHeader>
                <UI_BODY_BOLD>Connection</UI_BODY_BOLD>
                <StatusWrap>
                  <UI_BODY_SM style={{ color: colors2.greenText }}>Active</UI_BODY_SM>
                  <DisplayIcon icon="CheckCircle" size={16} color={colors2.green} />
                </StatusWrap>
              </CardHeader>
              <Row>
                <UI_BODY_SM style={{ color: colors2.grey60 }}>Account</UI_BODY_SM>
                <UI_BODY_SM>{conn.providerAccountName || "—"}</UI_BODY_SM>
              </Row>
              {conn.adminEmail && (
                <Row>
                  <UI_BODY_SM style={{ color: colors2.grey60 }}>Authorized by</UI_BODY_SM>
                  <UI_BODY_SM>
                    {conn.authorizedBy?.name ? `${conn.authorizedBy.name} (${conn.adminEmail})` : conn.adminEmail}
                  </UI_BODY_SM>
                </Row>
              )}
              {connectedAt && (
                <Row>
                  <UI_BODY_SM style={{ color: colors2.grey60 }}>Connected since</UI_BODY_SM>
                  <UI_BODY_SM>{connectedAt}</UI_BODY_SM>
                </Row>
              )}
            </Card>

            <Card>
              <CardHeader>
                <UI_BODY_BOLD>Datasets</UI_BODY_BOLD>
                <UI_CAPTION style={{ color: colors2.grey60 }}>
                  {links.length === 1 ? "1 dataset" : `${links.length} datasets`}
                </UI_CAPTION>
              </CardHeader>
              {links.length === 0 ? (
                <EmptyState>
                  <UI_BODY_SM style={{ color: colors2.grey60 }}>
                    {`${name} is authenticated but not importing into any dataset yet.`}
                  </UI_BODY_SM>
                </EmptyState>
              ) : (
                links.map((link) => (
                  <DatasetRow
                    key={link.contentType}
                    onClick={() => setOpenContentType(link.contentType)}
                  >
                    <UI_BODY_SM>{titleFor(link.contentType)}</UI_BODY_SM>
                    <DisplayIcon icon="CaretRight" size={16} color={colors2.grey60} />
                  </DatasetRow>
                ))
              )}
            </Card>
          </>
        )}
      </Body>

      {/* Adding a dataset is an integration-level action, so it sits with the
          list rather than inside one dataset's settings. */}
      {!isLoading && conn && !isStale && !showingDataset && canPublish && onAddDatasetRequest && (
        <FooterBar>
          <ButtonPrimary onClick={() => onAddDatasetRequest(provider)}>
            Add new dataset
          </ButtonPrimary>
        </FooterBar>
      )}
    </Modal>
  );
};

export default IntegrationSettingsModal;
